import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import useEnv from '@zenbusiness/application-commons-env';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import * as mitekScienceSDK from '#public/mitekSDK/mitek-science-sdk';

import RenderContainer from './RenderContainer';

import {
  AUTO_CAPTURE_MODE,
  CAMERA_DISPLAY_STARTED_EVENT,
  CAPTURE_AND_PROCESS_FRAME_COMMAND,
  CHECK_BACK_DOC_TYPE,
  CHECK_FRONT_DOC_TYPE,
  ERROR_EVENT,
  FRAME_CAPTURE_RESULT_EVENT,
  FRAME_PROCESSING_FEEDBACK_EVENT,
  FRAME_PROCESSING_STARTED_EVENT,
  MANUAL_CAPTURE_MODE,
  MESSAGES,
  MITEK_ERROR_FOUR_CORNER_EVENT,
  SHOW_HINT_EVENT,
  STOP_COMMAND
} from './constants';
import { generateDynamicDotByPath } from './helper';
import { useMitekErrorHandler } from '../useMitekErrorHandler';

export const MitekSdkContext = createContext();
MitekSdkContext.displayName = 'MitekSdkContext';

const useMitekSdkContext = () => {
  const context = useContext(MitekSdkContext);

  if (typeof context === 'undefined') {
    throw new Error('useMitekSdkContext must be used within a MitekSdkProvider');
  }

  return context;
};

export function MitekSdkProvider({
  children,
  handleError = null,
  mitekOptions = {},
  renderContainer = null,
  timerValue = 999999
}) {
  const { MITEK_MISNAP_MOBILE_WEB_SDK_VERSION } = useEnv('head-first');
  const captureException = useCaptureException();
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const { mitekErrorHandler } = useMitekErrorHandler();

  const [checkBackImageResult, setCheckBackImageResult] = useState(null);
  const [checkFrontImageResult, setCheckFrontImageResult] = useState(null);
  const [isCaptureInProgress, setIsCaptureInProgress] = useState(false);
  const [isMitekSdkReady, setIsMitekSdkReady] = useState(false);
  const [isAutoCaptureMode, setIsAutoCaptureMode] = useState(true);

  const [isLoadingFrontImage, setIsLoadingFrontImage] = useState(false);
  const [isLoadingBackImage, setIsLoadingBackImage] = useState(false);
  const [warningFrontImage, setWarningFrontImage] = useState('');
  const [warningBackImage, setWarningBackImage] = useState('');
  const [isAllowCameraPermission, setIsAllowCameraPermission] = useState(false);

  const { pathname } = useLocation();

  const defaultCaptureInitializationOptions = useMemo(
    () => ({
      mode: isAutoCaptureMode ? AUTO_CAPTURE_MODE : MANUAL_CAPTURE_MODE,
      // Despite what the mitek docs say and afaict, absolute
      // URLs do not work for the path value; therefore, we
      // attempt to access the mitekSDK hosted on the CDN
      // via relative URL going up three directories assuming
      // we are three levels deep for the banking-ui dashboard
      // e.g. /d/b/{biz uuid} => 1/2/3 => ../../../
      mitekSDKPath: `${generateDynamicDotByPath(
        pathname,
        '/'
      )}cdn-assets/banking-ui/static/js/mitekSDK/${MITEK_MISNAP_MOBILE_WEB_SDK_VERSION}/`,
      options: {
        qualityPercent: 90, // value between 50 to 100 recommended
        guidePaddingLevel: 1, // value should be between 1 and 3
        disableSmileDetection: false, // disable smile detection
        videoContainerId: 'banking-ui-mitek-sdk-container' // (optionally) embed the video in a custom HTML container
      }
    }),
    [MITEK_MISNAP_MOBILE_WEB_SDK_VERSION, isAutoCaptureMode, pathname]
  );

  const stopImageCapture = useCallback(() => {
    mitekScienceSDK.cmd(STOP_COMMAND);
    setIsCaptureInProgress(false);
    setIsMitekSdkReady(false);
  }, []);

  const throwError = useCallback((documentType) => {
    if (![CHECK_BACK_DOC_TYPE, CHECK_FRONT_DOC_TYPE].includes(documentType)) {
      throw new Error(
        `A valid documentType argument object must be provided - the argument ${documentType} is invalid.`
      );
    }
  }, []);

  const saveImageHandler = useCallback((result, args) => {
    clearTimeout(timerRef.current);
    setIsMitekSdkReady(false);
    setIsCaptureInProgress(false);
    const imageData = result?.response?.imageData;
    const docType = result?.response?.docType;
    const warnings = result?.response?.warnings;
    if (imageData && docType) {
      switch (docType) {
        case CHECK_FRONT_DOC_TYPE:
          setCheckFrontImageResult(imageData);
          setIsLoadingFrontImage(false);
          break;
        case CHECK_BACK_DOC_TYPE:
          setCheckBackImageResult(imageData);
          setIsLoadingBackImage(false);
          break;
        default:
          break;
      }
      // the callback to store imaged into other places
      if (typeof args?.callback === 'function') {
        args?.callback(imageData, docType);
      }
    }

    // After mitekSDK has finished validating the image and
    // there is an image with an error, warnings will be returned
    if (warnings && docType) {
      switch (docType) {
        case CHECK_FRONT_DOC_TYPE:
          setWarningFrontImage(MESSAGES[warnings.key]);
          break;
        case CHECK_BACK_DOC_TYPE:
          setWarningBackImage(MESSAGES[warnings.key]);
          break;
        default:
          break;
      }
    }
  }, []);

  const errorHandler = useCallback(
    (errors) => {
      captureException(Array.isArray(errors) ? errors[0] : errors);
      setIsMitekSdkReady(false);
      if (typeof handleError === 'function') {
        handleError(errors, stopImageCapture);
      }
    },
    [captureException, handleError, stopImageCapture]
  );

  const startAutoCaptureCheckImage = useCallback(
    (args) => {
      const documentType = args?.documentType || CHECK_FRONT_DOC_TYPE;

      throwError(documentType);
      setIsCaptureInProgress(true);

      // TODO: make this selector more specific, or even better
      // make this declarative with React, if possible
      document?.querySelector('div video[autoplay="true"]')?.play();
      // start an auto-capture session
      mitekScienceSDK.cmd(
        CAPTURE_AND_PROCESS_FRAME_COMMAND,
        Object.assign(defaultCaptureInitializationOptions, mitekOptions, { documentType })
      );

      mitekScienceSDK.on(ERROR_EVENT, (error) => {
        errorHandler(error);
      });

      mitekScienceSDK.on(CAMERA_DISPLAY_STARTED_EVENT, () => {
        /* (optionally) launch camera in full screen
      This example from mitek docs may be worth testing - leaving it disabled for now
      if (typeof screenfull != "undefined" && screenfull.isEnabled) {
        screenfull.request();
      }
      */
        const message = MESSAGES[documentType]?.INIT_EVENT;
        if (message) {
          mitekScienceSDK.cmd(SHOW_HINT_EVENT, MESSAGES[documentType].INIT_EVENT);
        }
      });

      mitekScienceSDK.on(FRAME_PROCESSING_FEEDBACK_EVENT, (result) => {
        const latestMessage = result.key;

        if (latestMessage !== null) {
          let hintMessage = MESSAGES[latestMessage] || latestMessage;

          // use a custom message for four corners not found
          if (latestMessage === MITEK_ERROR_FOUR_CORNER_EVENT) {
            hintMessage = MESSAGES[documentType].MITEK_ERROR_FOUR_CORNER_EVENT;
          }

          mitekScienceSDK.cmd(SHOW_HINT_EVENT, hintMessage);
        }
      });

      mitekScienceSDK.on(FRAME_CAPTURE_RESULT_EVENT, (result) => {
        /* exit full screen
      This example from mitek docs may be worth testing - leaving it disabled for now
        if (typeof screenfull != "undefined" && screenfull.isEnabled) {
          screenfull.exit();
        }
        */
        saveImageHandler(result, args);
      });

      mitekScienceSDK.on(FRAME_PROCESSING_STARTED_EVENT, () => {
        setIsMitekSdkReady(true);

        timerRef.current = setTimeout(() => {
          const errorObj = {
            name: 'timeout',
            message: `Image capture process has timed out. Please try to capture image within ${
              timerValue / 1000
            } seconds.`
          };

          stopImageCapture();
          if (typeof handleError === 'function') {
            handleError(errorObj);
          }
        }, timerValue);
      });
    },
    [
      defaultCaptureInitializationOptions,
      throwError,
      handleError,
      mitekOptions,
      stopImageCapture,
      saveImageHandler,
      errorHandler,
      timerValue
    ]
  );

  const startManualCaptureCheckImage = useCallback(
    (args) => {
      const documentType = args?.documentType || CHECK_FRONT_DOC_TYPE;
      throwError(documentType);

      // TODO: make this selector more specific, or even better
      // make this declarative with React, if possible
      // start an auto-capture session
      mitekScienceSDK.cmd(
        CAPTURE_AND_PROCESS_FRAME_COMMAND,
        Object.assign(defaultCaptureInitializationOptions, mitekOptions, { documentType })
      );

      mitekScienceSDK.on('FRAME_PROCESSING_FEEDBACK', () => {
        setIsLoadingFrontImage(false);
        setIsLoadingBackImage(false);
      });

      mitekScienceSDK.on('IMAGE_CAPTURED', () => {
        if (documentType === CHECK_FRONT_DOC_TYPE) {
          setIsLoadingFrontImage(true);
        } else {
          setIsLoadingBackImage(true);
        }
      });

      mitekScienceSDK.on('FRAME_CAPTURE_RESULT', (result) => {
        saveImageHandler(result, args);
      });

      mitekScienceSDK.on(ERROR_EVENT, (error) => {
        errorHandler(error);
      });

      mitekScienceSDK.on(FRAME_PROCESSING_STARTED_EVENT, () => {});
    },
    [throwError, defaultCaptureInitializationOptions, mitekOptions, saveImageHandler, errorHandler]
  );

  const autoCaptureCheckImage = useCallback(
    (documentType, callback) =>
      isAllowCameraPermission
        ? startAutoCaptureCheckImage({ documentType, callback })
        : mitekErrorHandler([{ code: 113 }]),
    [isAllowCameraPermission, mitekErrorHandler, startAutoCaptureCheckImage]
  );

  const manualCaptureCheckImage = useCallback(
    (documentType, callback) =>
      isAllowCameraPermission
        ? startManualCaptureCheckImage({ documentType, callback })
        : mitekErrorHandler([{ code: 113 }]),
    [isAllowCameraPermission, mitekErrorHandler, startManualCaptureCheckImage]
  );

  const autoCaptureCheckImageFront = useCallback(
    (callback) => autoCaptureCheckImage(CHECK_FRONT_DOC_TYPE, callback),
    [autoCaptureCheckImage]
  );

  const autoCaptureCheckImageBack = useCallback(
    (callback) => autoCaptureCheckImage(CHECK_BACK_DOC_TYPE, callback),
    [autoCaptureCheckImage]
  );

  const manualCaptureCheckImageFront = useCallback(
    (callback) => manualCaptureCheckImage(CHECK_FRONT_DOC_TYPE, callback),
    [manualCaptureCheckImage]
  );

  const manualCaptureCheckImageBack = useCallback(
    (callback) => manualCaptureCheckImage(CHECK_BACK_DOC_TYPE, callback),
    [manualCaptureCheckImage]
  );

  const resetAllImageCaptured = useCallback(() => {
    setCheckFrontImageResult(null);
    setCheckBackImageResult(null);
  }, []);

  const switchCaptureMode = useCallback(() => {
    stopImageCapture();
    setIsAutoCaptureMode((mode) => !mode);
  }, [stopImageCapture]);

  /**
   * Checking media of devices (devices support camera or not)
   * If not then show popup permission deny (113 is mitek permission denied)
   * If devices support a camera then ask camera permission
   * ===> If cancel then show popup permission deny
   * ===> If allow then set permission to allow
   */
  const checkCameraPermission = useCallback(() => {
    if ('mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          setIsAllowCameraPermission(true);
        })
        .catch((error) => {
          captureException(error, { level: 'warning' });
          mitekErrorHandler([{ code: 113 }]);
        });
    } else {
      mitekErrorHandler([{ code: 113 }]);
    }
  }, [captureException, mitekErrorHandler]);

  const initiateMitekSdkCommand = useCallback(
    (...args) =>
      () =>
        mitekScienceSDK.cmd(...args),
    []
  );

  const value = useMemo(
    () => ({
      autoCaptureCheckImageBack,
      autoCaptureCheckImageFront,
      manualCaptureCheckImageBack,
      manualCaptureCheckImageFront,
      checkBackImageResult,
      checkFrontImageResult,
      initiateMitekSdkCommand,
      isCaptureInProgress,
      isMitekSdkReady,
      startAutoCaptureCheckImage,
      stopImageCapture,
      resetAllImageCaptured,
      setCheckFrontImageResult,
      setCheckBackImageResult,
      switchCaptureMode,
      isAutoCaptureMode,
      isLoadingFrontImage,
      isLoadingBackImage,
      warningFrontImage,
      warningBackImage,
      setIsAutoCaptureMode,
      checkCameraPermission,
      isAllowCameraPermission
    }),
    [
      autoCaptureCheckImageBack,
      autoCaptureCheckImageFront,
      manualCaptureCheckImageBack,
      manualCaptureCheckImageFront,
      checkBackImageResult,
      checkFrontImageResult,
      initiateMitekSdkCommand,
      isCaptureInProgress,
      isMitekSdkReady,
      startAutoCaptureCheckImage,
      stopImageCapture,
      resetAllImageCaptured,
      setCheckFrontImageResult,
      setCheckBackImageResult,
      switchCaptureMode,
      isAutoCaptureMode,
      isLoadingFrontImage,
      isLoadingBackImage,
      warningFrontImage,
      warningBackImage,
      setIsAutoCaptureMode,
      checkCameraPermission,
      isAllowCameraPermission
    ]
  );

  return (
    <MitekSdkContext.Provider value={value}>
      {children}
      <RenderContainer
        isCaptureInProgress={isCaptureInProgress}
        isMitekSdkReady={isMitekSdkReady}
        renderContainer={renderContainer}
        stopImageCapture={stopImageCapture}
      />
    </MitekSdkContext.Provider>
  );
}

export default useMitekSdkContext;

MitekSdkProvider.propTypes = {
  handleError: PropTypes.func,
  mitekOptions: PropTypes.shape({
    mode: PropTypes.string, // See mitek docs
    mitekSDKPath: PropTypes.string, // See mitek docs
    options: PropTypes.shape({
      // value between 50 to 100 recommended
      qualityPercent: PropTypes.number,
      // how long the hint message displays (in MS) / note: 2400 recommended for screen readers
      hintFrequencyMS: PropTypes.number,
      // screen reader setting for hint updates value (0=off / 1=polite / 2=assertive)
      hintAriaLive: PropTypes.number,
      // size of the overlay guide (1=large, 2=medium, 3=small) / note: 1 is recommended
      guidePaddingLevel: PropTypes.number,
      // set the size of the hint message (1=small / 2=medium / 3=large)
      hintMessageSize: PropTypes.number,
      // disable smile detection
      disableSmileDetection: PropTypes.bool,
      // (optionally) embed the video in a custom HTML container
      videoContainerId: PropTypes.string
    })
  }),
  // Where to render the image capture container
  renderContainer: PropTypes.oneOf([PropTypes.node, PropTypes.func]),
  // Timeout value to close image capture mode
  timerValue: PropTypes.number
};
